import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Plus, Minus, Video, Search, Loader2, Paperclip, PenLine,
    Filter as FilterIcon, Bell, ChevronLeft, ChevronRight, Eye,
    Calendar as CalendarIcon, Users, Mail, Clock, Check, Trash2, UserPlus
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "primereact/calendar";
import EmailComposer from "../EmailComposer";
import ClassSchedulingConfig from "./ClassSchedulingConfig";
import CsvStudentUploader from "./CsvStudentUploader";
import FormationStudentPreview from "./FormationStudentPreview";
import NotificationAttachments, { AttachmentInfo } from "./NotificationAttachments";
import { Filter as StudentFilter } from "../FormationFilterEditor";
import { Notification, VirtualClass, CsvStudent } from "@/pages/VirtualClasses";
import { cn, decodeHtmlEntities, toParisNaive } from "@/lib/utils";

interface ClassConfigModalProps {
    mode: 'create' | 'edit';
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;

    // Core Data
    initialData?: VirtualClass | null; // For Edit Mode
    className: string;
    setClassName: (val: string) => void;
    organizerEmail: string;
    setOrganizerEmail: (val: string) => void;
    startTime: string;
    setStartTime: (val: string) => void;
    endTime: string;
    setEndTime: (val: string) => void;
    classUrl?: string; // Edit mode only usually
    setClassUrl?: (val: string) => void;

    // Co-professors
    coProfessors: string[];
    setCoProfessors: (val: string[]) => void;

    // Signature settings
    signatureAtStart: boolean;
    setSignatureAtStart: (val: boolean) => void;
    signingDeadline: string;
    setSigningDeadline: (val: string) => void;

    // Student Source
    studentSource: 'learnybox' | 'csv';
    setStudentSource: (val: 'learnybox' | 'csv') => void;
    csvStudents: CsvStudent[];
    setCsvStudents: (students: CsvStudent[]) => void;

    // Formations (Targeting)
    formations: any[];
    isFetchingFormations: boolean;
    formationSearch: string;
    setFormationSearch: (val: string) => void;
    selectedFormations: number[];
    setSelectedFormations: (val: number[]) => void;
    formationFilters: Record<number, StudentFilter[]>;
    setEditingFiltersFor: (id: number | null) => void;
    excludedStudents: Record<number, string[]>;
    setExcludedStudents: (val: Record<number, string[]>) => void;
    token: string | null;

    // Notifications
    sendInvitations: boolean;
    setSendInvitations: (val: boolean) => void;
    notifications: Notification[];
    addNotification: () => void;
    removeNotification: (id: string) => void;
    updateNotification: (id: string, updates: Partial<Notification>) => void;

    // Attachments
    pendingAttachments: Record<string, File[]>;
    setPendingAttachments: (val: Record<string, File[]>) => void;
    existingAttachments: AttachmentInfo[];
    deleteExistingAttachment: (attachmentId: number) => void;

    // Submission & UI
    isSubmitting: boolean;
    onSubmit: (e: any) => void;
}

const ClassConfigModal: React.FC<ClassConfigModalProps> = ({
    mode,
    isOpen,
    onOpenChange,
    initialData,
    className,
    setClassName,
    organizerEmail,
    setOrganizerEmail,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    classUrl,
    setClassUrl,
    coProfessors,
    setCoProfessors,
    signatureAtStart,
    setSignatureAtStart,
    signingDeadline,
    setSigningDeadline,
    studentSource,
    setStudentSource,
    csvStudents,
    setCsvStudents,
    formations,
    isFetchingFormations,
    formationSearch,
    setFormationSearch,
    selectedFormations,
    setSelectedFormations,
    formationFilters,
    setEditingFiltersFor,
    excludedStudents,
    setExcludedStudents,
    token,
    sendInvitations,
    setSendInvitations,
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    pendingAttachments,
    setPendingAttachments,
    existingAttachments,
    deleteExistingAttachment,
    isSubmitting,
    onSubmit
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [activeNotificationIndex, setActiveNotificationIndex] = useState(0);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [dialogNode, setDialogNode] = useState<HTMLDivElement | null>(null);

    // Reset step when opening
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
            if (mode === 'edit' && initialData) {
                // Logic handled by parent usually, but ensure consistency
                // Parent component should populate props based on initialData
            }
        }
    }, [isOpen, mode, initialData]);

    const handleNextStep = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentStep(prev => prev + 1);
            setIsTransitioning(false);
        }, 300);
    };

    const handlePrevStep = () => {
        setCurrentStep(prev => prev - 1);
    };

    // Helper to format minutes into a readable format
    const formatMinutes = (minutes: number): string => {
        const days = Math.floor(minutes / (24 * 60));
        const hours = Math.floor((minutes % (24 * 60)) / 60);
        const mins = minutes % 60;

        const parts: string[] = [];
        if (days > 0) parts.push(`${days}j`);
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}min`);

        return parts.length > 0 ? parts.join(' ') : '0min';
    };

    const isInvitationConfigValid = !sendInvitations || (
        notifications.length > 0 &&
        notifications.every(notif =>
            notif.subject.trim() !== "" &&
            notif.body.trim() !== "" &&
            (notif.scheduling.type === 'fixed' ? notif.scheduling.date : (notif.scheduling.offset !== undefined && notif.scheduling.offset >= 0))
        )
    );

    const getReviewData = () => {
        const selectedFormationsNames = formations
            .filter(f => selectedFormations.includes(f.id))
            .map(f => f.titre || f.nom || f.name);

        return {
            selectedFormationsNames,
        };
    };

    const reviewData = getReviewData();

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent
                ref={setDialogNode}
                className="sm:max-w-[92vw] h-[96vh] p-0 flex flex-col rounded-3xl border-none shadow-2xl bg-white"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader className="p-8 pb-4 bg-slate-50/50 border-b border-slate-100 relative rounded-t-3xl">
                    <div className="flex justify-between items-start">
                        <div className="space-y-1 flex-1">
                            <DialogTitle className="text-2xl font-black text-slate-900 flex items-center gap-3">
                                <div className={`h-10 w-10 ${currentStep === 2 ? 'bg-emerald-500' : 'bg-primary'} text-white rounded-xl flex items-center justify-center shadow-lg ${currentStep === 2 ? 'shadow-emerald-500/20' : 'shadow-primary/20'}`}>
                                    {currentStep === 2 ? <Bell className="h-6 w-6" /> : mode === 'edit' ? <Video className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
                                </div>
                                {mode === 'edit' ? "Modifier la classe" : "Nouvelle Classe"}
                                {currentStep === 2 && (
                                    <div className="flex flex-1 items-center gap-4 ml-4">
                                        <div className="flex items-center gap-3 bg-slate-50 p-2 px-3 rounded-xl border border-slate-100">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${sendInvitations ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                {sendInvitations ? "Emails Activés" : "Emails Désactivés"}
                                            </span>
                                            <Switch
                                                checked={sendInvitations}
                                                onCheckedChange={setSendInvitations}
                                                className="data-[state=checked]:bg-emerald-500"
                                            />
                                        </div>
                                    </div>
                                )}
                            </DialogTitle>
                        </div>

                        <div className="flex items-center gap-2 bg-white border border-slate-100 rounded-2xl p-2 pr-4 shadow-sm">
                            {[1, 2, 3].map(step => (
                                <React.Fragment key={step}>
                                    <div className={`h-8 w-8 rounded-xl flex items-center justify-center font-black text-xs transition-all ${currentStep === step ? 'bg-primary text-white shadow-md shadow-primary/20' : currentStep > step ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                        {currentStep > step ? <Check className="h-4 w-4" /> : step}
                                    </div>
                                    {step < 3 && <div className={`w-4 h-[1px] ${currentStep > step ? 'bg-emerald-200' : 'bg-slate-100'}`}></div>}
                                </React.Fragment>
                            ))}
                            <span className="ml-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                {currentStep === 1 ? "Infos" : currentStep === 2 ? "Emails" : "Valider"}
                            </span>
                        </div>
                    </div>
                </DialogHeader>

                <div className={`flex-1 p-8 py-0 ${currentStep === 1 ? 'overflow-y-hidden' : 'overflow-y-auto'} custom-scrollbar`}>
                    <div className="h-full">
                        {/* STEP 1: INFOS & TARGETING */}
                        {currentStep === 1 && (
                            <div className="h-full grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="space-y-6">
                                    <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-6">
                                        <h3 className="text-sm font-bold text-slate-900 border-b border-slate-50 pb-4 flex items-center gap-2">
                                            <div className="h-2 w-2 rounded-full bg-primary"></div>
                                            Détails de la Session
                                        </h3>

                                        <div className="space-y-6">
                                            <div className="space-y-2">
                                                <Label htmlFor="create-name" className="text-xs font-bold text-slate-700 ml-1">NOM DE LA CLASSE</Label>
                                                <Input
                                                    id="create-name"
                                                    placeholder="Ex: Coaching Hebdomadaire #5"
                                                    value={className}
                                                    onChange={(e) => setClassName(e.target.value)}
                                                    required
                                                    className="h-12 bg-slate-50/50 border-slate-200 rounded-2xl focus:ring-primary/20 text-base"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="create-email" className="text-xs font-bold text-slate-700 ml-1">EMAIL ORGANISATEUR</Label>
                                                <Input
                                                    id="create-email"
                                                    placeholder="votre@email.com"
                                                    value={organizerEmail}
                                                    onChange={(e) => setOrganizerEmail(e.target.value)}
                                                    required
                                                    className="h-12 bg-slate-50/50 border-slate-200 rounded-2xl focus:ring-primary/20"
                                                />
                                            </div>

                                            {/* Co-professors */}
                                            <div className="space-y-2">
                                                <Label className="text-xs font-bold text-slate-700 ml-1 flex items-center gap-1.5">
                                                    <UserPlus className="h-3.5 w-3.5" /> CO-PROFESSEURS
                                                </Label>
                                                <div className="space-y-2">
                                                    {coProfessors.map((email, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <Input
                                                                type="email"
                                                                placeholder="co-professeur@email.com"
                                                                value={email}
                                                                onChange={(e) => {
                                                                    const updated = [...coProfessors];
                                                                    updated[index] = e.target.value;
                                                                    setCoProfessors(updated);
                                                                }}
                                                                className="h-10 bg-slate-50/50 border-slate-200 rounded-xl focus:ring-primary/20 text-sm"
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-10 w-10 shrink-0 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                                                                onClick={() => setCoProfessors(coProfessors.filter((_, i) => i !== index))}
                                                            >
                                                                <Minus className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-xs font-bold text-slate-500 hover:text-primary h-8 px-3 rounded-lg"
                                                        onClick={() => setCoProfessors([...coProfessors, ''])}
                                                    >
                                                        <Plus className="h-3.5 w-3.5 mr-1.5" /> Ajouter un co-professeur
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <Label className="text-xs font-bold text-slate-700 ml-1 flex items-center gap-1.5">
                                                        <PenLine className="h-3.5 w-3.5" /> SIGNATURE OBLIGATOIRE AVANT ACCÈS
                                                    </Label>
                                                    <Switch
                                                        checked={signatureAtStart}
                                                        onCheckedChange={setSignatureAtStart}
                                                    />
                                                </div>
                                                {!signatureAtStart && (
                                                    <>
                                                        <p className="text-[10px] text-slate-500 ml-1">
                                                            Les étudiants pourront rejoindre la session sans signer. Un lien de signature séparé sera généré.
                                                        </p>
                                                        <div className="space-y-1">
                                                            <Label className="text-[10px] font-bold text-slate-500 ml-1 uppercase tracking-wider">
                                                                Date limite de signature (optionnel)
                                                            </Label>
                                                            <Input
                                                                type="datetime-local"
                                                                value={signingDeadline}
                                                                onChange={(e) => setSigningDeadline(e.target.value)}
                                                                className="h-10 bg-slate-50/50 border-slate-200 rounded-xl focus:ring-primary/20 text-sm"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {mode === 'edit' && setClassUrl && (
                                                <div className="space-y-2">
                                                    <Label htmlFor="class-url" className="text-xs font-bold text-slate-700 ml-1">LIEN GOOGLE MEET</Label>
                                                    <Input
                                                        id="class-url"
                                                        value={classUrl}
                                                        onChange={(e) => setClassUrl(e.target.value)}
                                                        className="h-12 bg-slate-50/50 border-slate-200 rounded-2xl focus:ring-primary/20"
                                                    />
                                                </div>
                                            )}

                                            <div className="flex items-start gap-8">
                                                <div className="space-y-2 w-1/2">
                                                    <Label className="text-xs font-bold text-slate-700 ml-1">DATE</Label>
                                                    <Calendar
                                                        minDate={new Date()}
                                                        value={startTime ? new Date(startTime) : new Date()}
                                                        onChange={(e) => {
                                                            const date = e.value as Date;
                                                            if (!date) return;
                                                            const getValidDate = (val: string) => {
                                                                const d = new Date(val);
                                                                return isNaN(d.getTime()) ? new Date() : d;
                                                            };
                                                            const currentStart = getValidDate(startTime);
                                                            const newStart = new Date(date);
                                                            newStart.setHours(currentStart.getHours(), currentStart.getMinutes(), 0, 0);
                                                            setStartTime(toParisNaive(newStart));

                                                            const currentEnd = getValidDate(endTime);
                                                            const newEnd = new Date(date);
                                                            newEnd.setHours(currentEnd.getHours(), currentEnd.getMinutes(), 0, 0);
                                                            setEndTime(toParisNaive(newEnd));
                                                        }}
                                                        dateFormat="dd/mm/yy"
                                                        showIcon
                                                        className="w-full"
                                                        inputClassName="p-inputtext w-full h-12 bg-slate-50/50 border border-slate-200 rounded-2xl text-base font-medium px-4 focus:ring-primary/20"
                                                        readOnlyInput
                                                        appendTo={dialogNode || 'self'}
                                                    />
                                                </div>

                                                <div className="flex flex-col gap-1 w-1/2">
                                                    <div className="flex gap-4">
                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-bold text-slate-700 ml-1">DÉBUT</Label>
                                                            <Calendar
                                                                value={startTime ? new Date(startTime) : new Date()}
                                                                onChange={(e) => {
                                                                    const date = e.value as Date;
                                                                    if (!date) return;
                                                                    const current = startTime ? new Date(startTime) : new Date();
                                                                    const newStart = isNaN(current.getTime()) ? new Date() : new Date(current);
                                                                    newStart.setHours(date.getHours(), date.getMinutes(), 0, 0);
                                                                    setStartTime(toParisNaive(newStart));
                                                                }}
                                                                timeOnly
                                                                hourFormat="24"
                                                                showIcon
                                                                icon={() => <Clock className="h-4 w-4" />}
                                                                className="w-full"
                                                                inputClassName="p-inputtext w-full h-12 bg-slate-50/50 border border-slate-200 rounded-2xl text-base font-medium px-4 focus:ring-primary/20"
                                                                appendTo={dialogNode || 'self'}
                                                                readOnlyInput
                                                            />
                                                        </div>

                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-bold text-slate-700 ml-1">FIN</Label>
                                                            <Calendar
                                                                value={endTime ? new Date(endTime) : new Date()}
                                                                onChange={(e) => {
                                                                    const date = e.value as Date;
                                                                    if (!date) return;
                                                                    const current = endTime ? new Date(endTime) : new Date();
                                                                    const newEnd = isNaN(current.getTime()) ? new Date() : new Date(current);
                                                                    newEnd.setHours(date.getHours(), date.getMinutes(), 0, 0);
                                                                    setEndTime(toParisNaive(newEnd));
                                                                }}
                                                                timeOnly
                                                                hourFormat="24"
                                                                showIcon
                                                                icon={() => <Clock className="h-4 w-4" />}
                                                                className="w-full"
                                                                inputClassName="p-inputtext w-full h-12 bg-slate-50/50 border border-slate-200 rounded-2xl text-base font-medium px-4 focus:ring-primary/20"
                                                                appendTo={dialogNode || 'self'}
                                                                readOnlyInput
                                                            />
                                                        </div>
                                                    </div>

                                                    {new Date(endTime) <= new Date(startTime) && (
                                                        <p className="text-xs text-red-500 font-bold ml-1 flex items-center gap-1 animate-in slide-in-from-top-1 w-full">
                                                            <span className="w-1 h-1 rounded-full bg-red-500 inline-block" />
                                                            Fin invalide
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {mode === 'create' && (
                                        <div className="bg-blue-50/30 border border-blue-100/50 rounded-3xl p-6 flex items-start gap-4">
                                            <div className="h-10 w-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
                                                <Video className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-blue-900">Google Meet Auto-généré</p>
                                                <p className="text-xs text-blue-700/70 leading-relaxed mt-1">Le lien de la classe sera créé automatiquement lors de la validation du brouillon.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-6 overflow-y-hidden">
                                    <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm flex flex-col h-full overflow-y-hidden">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-50 pb-4 flex items-center gap-2">
                                                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                                                Ciblage Étudiants {mode === 'edit' && <span className="text-slate-400 text-xs font-normal ml-2">(Lecture seule)</span>}
                                            </h3>

                                            {mode === 'create' && studentSource === 'learnybox' && (
                                                <div className="relative w-48">
                                                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                                                    <Input
                                                        placeholder="Filtrer..."
                                                        className="h-8 pl-8 text-[11px] bg-white border-slate-200 rounded-lg focus:ring-primary/10"
                                                        value={formationSearch}
                                                        onChange={(e) => setFormationSearch(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Mode toggle tabs — only in create mode */}
                                        {mode === 'create' && (
                                            <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mt-2 mb-2">
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        "flex-1 text-xs font-bold py-2 px-3 rounded-lg transition-all",
                                                        studentSource === 'learnybox'
                                                            ? "bg-white text-slate-900 shadow-sm"
                                                            : "text-slate-500 hover:text-slate-700"
                                                    )}
                                                    onClick={() => setStudentSource('learnybox')}
                                                >
                                                    Learnybox
                                                </button>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        "flex-1 text-xs font-bold py-2 px-3 rounded-lg transition-all",
                                                        studentSource === 'csv'
                                                            ? "bg-white text-slate-900 shadow-sm"
                                                            : "text-slate-500 hover:text-slate-700"
                                                    )}
                                                    onClick={() => setStudentSource('csv')}
                                                >
                                                    Fichier CSV
                                                </button>
                                            </div>
                                        )}

                                        <div className="flex-1 flex flex-col mt-4 space-y-4 overflow-y-hidden">
                                            {mode === 'edit' ? (
                                                <div className="h-full border border-slate-100 rounded-2xl p-4 overflow-y-auto bg-slate-50/30 space-y-2">
                                                    <p className="text-sm text-slate-500 p-4 text-center italic">Le ciblage ne peut pas être modifié après la création car des invitations ont déjà pu être envoyées.</p>
                                                    {studentSource === 'csv' ? (
                                                        <CsvStudentUploader students={csvStudents} onStudentsChange={setCsvStudents} readOnly />
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2 justify-center">
                                                            {reviewData.selectedFormationsNames.map((name, i) => (
                                                                <Badge key={i} variant="secondary" className="bg-white text-slate-600 border border-slate-200 rounded-lg px-2 py-1 text-[10px] font-bold">
                                                                    {decodeHtmlEntities(name)}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : studentSource === 'csv' ? (
                                                <div className="h-full border border-slate-100 rounded-2xl p-4 overflow-y-auto bg-slate-50/30">
                                                    <CsvStudentUploader students={csvStudents} onStudentsChange={setCsvStudents} />
                                                </div>
                                            ) : (
                                                <div className="h-full border border-slate-100 rounded-2xl p-4 overflow-y-auto bg-slate-50/30 space-y-2">
                                                    {isFetchingFormations ? (
                                                        <div className="flex justify-center py-8">
                                                            <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                                                        </div>
                                                    ) : formations.length === 0 ? (
                                                        <p className="text-xs text-slate-400 text-center py-4 italic">Aucune formation trouvée</p>
                                                    ) : (
                                                        formations
                                                            .filter(f => {
                                                                const name = (f.titre || f.nom || f.name || "").toLowerCase();
                                                                return decodeHtmlEntities(name).includes(formationSearch.toLowerCase());
                                                            })
                                                            .map((f) => (
                                                                <React.Fragment key={f.id}>
                                                                    <div className="flex items-center space-x-3 py-2 px-3 rounded-xl hover:bg-white border border-transparent hover:border-slate-100 transition-all group">
                                                                        <Checkbox
                                                                            id={`f-${f.id}`}
                                                                            checked={selectedFormations.includes(f.id)}
                                                                            onCheckedChange={(checked: boolean) => {
                                                                                if (checked) setSelectedFormations([...selectedFormations, f.id]);
                                                                                else setSelectedFormations(selectedFormations.filter(id => id !== f.id));
                                                                            }}
                                                                            className="rounded-md h-5 w-5 border-slate-300 data-[state=checked]:bg-primary"
                                                                        />
                                                                        <label htmlFor={`f-${f.id}`} className="text-xs font-bold cursor-pointer truncate text-slate-600 flex-1 group-hover:text-slate-900 transition-colors">
                                                                            {decodeHtmlEntities(f.titre || f.nom || f.name || `Formation #${f.id}`)}
                                                                        </label>
                                                                        {selectedFormations.includes(f.id) && (
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    setEditingFiltersFor(f.id);
                                                                                }}
                                                                            >
                                                                                {formationFilters[f.id]?.length > 0 ? (
                                                                                    <Badge className="h-5 min-w-[20px] px-1.5 text-[10px] bg-primary border-none shadow-sm shadow-primary/20">
                                                                                        {formationFilters[f.id].length}
                                                                                    </Badge>
                                                                                ) : (
                                                                                    <FilterIcon className="h-3.5 w-3.5" />
                                                                                )}
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                    {selectedFormations.includes(f.id) && (
                                                                        <FormationStudentPreview
                                                                            formationId={f.id}
                                                                            filters={formationFilters[f.id] || []}
                                                                            excludedEmails={excludedStudents[f.id] || []}
                                                                            onExcludedChange={(emails) => setExcludedStudents({ ...excludedStudents, [f.id]: emails })}
                                                                            token={token}
                                                                        />
                                                                    )}
                                                                </React.Fragment>
                                                            ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: NOTIFICATIONS */}
                        {currentStep === 2 && (
                            <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-right-4 duration-500 h-full">
                                {sendInvitations ? (
                                    <div className="space-y-6 pb-8 h-full flex flex-col">
                                        {/* Tab Navigation */}
                                        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                            <div className="flex items-center gap-2 flex-1">
                                                {notifications.map((notif, index) => (
                                                    <button
                                                        key={notif.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveNotificationIndex(index);
                                                            setShowDeleteConfirm(false);
                                                        }}
                                                        className={`h-10 w-10 rounded-xl flex items-center justify-center font-black text-sm transition-all ${activeNotificationIndex === index
                                                            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                                                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                                                            }`}
                                                    >
                                                        {index + 1}
                                                    </button>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        addNotification();
                                                        setActiveNotificationIndex(notifications.length);
                                                    }}
                                                    className="h-10 w-10 rounded-xl flex items-center justify-center bg-slate-100 text-slate-400 hover:bg-emerald-50 hover:text-emerald-500 transition-all"
                                                >
                                                    <Plus className="h-5 w-5" />
                                                </button>
                                            </div>

                                            {/* Action buttons for active notification */}
                                            {notifications.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    {notifications.length > 1 && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="sm"
                                                            className={cn(
                                                                "h-8 text-xs font-bold transition-all duration-200",
                                                                showDeleteConfirm
                                                                    ? "text-white bg-red-500 hover:bg-red-500 hover:text-white px-3 rounded-lg"
                                                                    : "text-slate-400 hover:text-red-500"
                                                            )}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (showDeleteConfirm) {
                                                                    removeNotification(notifications[activeNotificationIndex].id);
                                                                    if (activeNotificationIndex >= notifications.length - 1) {
                                                                        setActiveNotificationIndex(Math.max(0, notifications.length - 2));
                                                                    }
                                                                    setShowDeleteConfirm(false);
                                                                } else {
                                                                    setShowDeleteConfirm(true);
                                                                }
                                                            }}
                                                            onMouseLeave={() => setShowDeleteConfirm(false)}
                                                        >
                                                            {showDeleteConfirm ? (
                                                                <span className="flex items-center gap-1.5 animate-in fade-in zoom-in-95 duration-200">
                                                                    <Trash2 className="h-3.5 w-3.5" /> Confirmer la suppression ?
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Supprimer cet email
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Active Notification Content */}
                                        {notifications.length > 0 && notifications[activeNotificationIndex] && (
                                            <div className="bg-white border border-slate-100 rounded-[32px] shadow-sm overflow-hidden flex-1 overflow-y-auto">
                                                <div className="flex p-8 py-4 gap-8 flex-col lg:flex-row">
                                                    <div className="space-y-4 w-full">
                                                        <div className="flex gap-8 items-start">
                                                            <div className="flex flex-col gap-2">
                                                                <h4 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Planification</h4>
                                                                <p className="text-xs text-primary/70 font-medium leading-relaxed max-w-xs">
                                                                    Quand cet email doit-il partir ?
                                                                </p>
                                                            </div>
                                                            <div className="min-w-[400px]">
                                                                <ClassSchedulingConfig
                                                                    scheduling={notifications[activeNotificationIndex].scheduling}
                                                                    onChange={(updates) => updateNotification(notifications[activeNotificationIndex].id, { scheduling: { ...notifications[activeNotificationIndex].scheduling, ...updates } })}
                                                                    appendTo={dialogNode || 'self'}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="border-t border-slate-100 pt-6">
                                                            <EmailComposer
                                                                subject={notifications[activeNotificationIndex].subject}
                                                                setSubject={(val) => updateNotification(notifications[activeNotificationIndex].id, { subject: val })}
                                                                body={notifications[activeNotificationIndex].body}
                                                                setBody={(val) => updateNotification(notifications[activeNotificationIndex].id, { body: val })}
                                                            />
                                                            <NotificationAttachments
                                                                notificationId={notifications[activeNotificationIndex].id}
                                                                pendingFiles={pendingAttachments[notifications[activeNotificationIndex].id] || []}
                                                                onPendingFilesChange={(files) =>
                                                                    setPendingAttachments({
                                                                        ...pendingAttachments,
                                                                        [notifications[activeNotificationIndex].id]: files,
                                                                    })
                                                                }
                                                                existingAttachments={existingAttachments.filter(
                                                                    a => a.notification_id === notifications[activeNotificationIndex].id
                                                                )}
                                                                onDeleteExisting={deleteExistingAttachment}
                                                                mode={mode}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex-1 bg-white border border-slate-100 rounded-[32px] p-16 text-center shadow-sm flex items-center justify-center">
                                        <div className="max-w-sm space-y-4">
                                            <div className="h-24 w-24 bg-slate-50 text-slate-200 rounded-full flex items-center justify-center mx-auto border border-dashed border-slate-200">
                                                <Bell className="h-12 w-12" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-xl font-black text-slate-400">Aucune séquence programmée</p>
                                                <p className="text-sm text-slate-400/70 leading-relaxed font-medium">
                                                    Activez l'envoi des emails pour configurer des messages de bienvenue et rappels automatiques.
                                                </p>
                                            </div>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="rounded-xl border-slate-200 text-slate-500 font-bold px-6"
                                                onClick={() => setSendInvitations(true)}
                                            >
                                                Activer maintenant
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* STEP 3: RECAP & SUBMIT */}
                        {currentStep === 3 && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-in fade-in slide-in-from-right-4 duration-500 max-w-6xl mx-auto h-full overflow-y-auto pt-4">
                                <div className="md:col-span-2 space-y-8 pb-8">
                                    <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-8 opacity-[0.03] scale-150">
                                            <Video className="h-24 w-24" />
                                        </div>
                                        <h3 className="text-[10px] font-black text-primary uppercase tracking-[0.2em] mb-4">Récapitulatif</h3>
                                        <div className="space-y-6">
                                            <h2 className="text-3xl font-black text-slate-900 leading-tight">{className}</h2>
                                            <div className="grid grid-cols-2 gap-8">
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Organisateur</p>
                                                    <p className="text-slate-700 font-semibold flex items-center gap-2 text-sm">
                                                        <Mail className="h-4 w-4 text-slate-300" /> {organizerEmail}
                                                    </p>
                                                </div>
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Horaire</p>
                                                    <p className="text-slate-700 font-semibold flex items-center gap-2 text-sm">
                                                        <CalendarIcon className="h-4 w-4 text-slate-300" />
                                                        {new Date(startTime).toLocaleString('fr-FR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                                                        {' - '}
                                                        {new Date(endTime).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                    </p>
                                                </div>
                                            </div>
                                            {coProfessors.filter(e => e.trim()).length > 0 && (
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Co-professeurs</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {coProfessors.filter(e => e.trim()).map((email, i) => (
                                                            <Badge key={i} variant="secondary" className="bg-violet-50 text-violet-700 border-violet-200 rounded-lg px-2 py-1 text-[10px] font-bold">
                                                                <UserPlus className="h-3 w-3 mr-1" /> {email}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Signature</p>
                                                <Badge variant="secondary" className={cn(
                                                    "rounded-lg px-2 py-1 text-[10px] font-bold",
                                                    signatureAtStart
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                        : "bg-amber-50 text-amber-700 border-amber-200"
                                                )}>
                                                    <PenLine className="h-3 w-3 mr-1" />
                                                    {signatureAtStart ? "Obligatoire avant accès" : "Lien séparé"}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm">
                                            <h3 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                                <Users className="h-3.5 w-3.5" /> Ciblage
                                            </h3>
                                            <div className="space-y-3">
                                                {studentSource === 'csv' ? (
                                                    <>
                                                        <p className="text-xs text-slate-500 font-medium">Source : Fichier CSV</p>
                                                        <Badge variant="secondary" className="bg-violet-50 text-violet-700 border-violet-200 rounded-lg px-3 py-1.5 text-xs font-bold">
                                                            {csvStudents.length} étudiant(s) depuis fichier CSV
                                                        </Badge>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p className="text-xs text-slate-500 font-medium">Formations sélectionnées ({selectedFormations.length}) :</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {reviewData.selectedFormationsNames.map((name, i) => (
                                                                <Badge key={i} variant="secondary" className="bg-slate-50 text-slate-600 border-none rounded-lg px-2 py-1 text-[10px] font-bold">
                                                                    {decodeHtmlEntities(name)}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm">
                                            <h3 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                                                <Bell className="h-3.5 w-3.5" /> Notifications
                                            </h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between bg-slate-50/50 p-3 rounded-2xl border border-slate-50">
                                                    <span className="text-xs font-bold text-slate-600">Envoi auto</span>
                                                    <Badge className={sendInvitations ? "bg-emerald-500" : "bg-slate-300"}>
                                                        {sendInvitations ? "ACTIVÉ" : "DÉSACTIVÉ"}
                                                    </Badge>
                                                </div>
                                                {sendInvitations && (
                                                    <div className="space-y-2">
                                                        <p className="text-xs text-slate-500 font-medium">Emails programmés ({notifications.length}) :</p>
                                                        <div className="space-y-2">
                                                            {notifications.map((notif, i) => (
                                                                <div key={notif.id} className="bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                                                                    <p className="text-[11px] font-black text-slate-700 truncate mb-1">{notif.subject}</p>
                                                                    <div className="flex items-center gap-3 text-[10px] text-slate-500 font-bold">
                                                                        <span className="flex items-center gap-1">
                                                                            <Clock className="h-3 w-3" />
                                                                            {notif.scheduling.type === 'fixed' ? `Le ${new Date(notif.scheduling.date || "").toLocaleString('fr-FR')}` : `${formatMinutes(notif.scheduling.offset || 0)} ${notif.scheduling.type.includes('before') ? 'avant' : 'après'} ${notif.scheduling.type.includes('start') ? 'début' : 'fin'}`}
                                                                        </span>
                                                                        {(() => {
                                                                            const count = (pendingAttachments[notif.id]?.length || 0) + existingAttachments.filter(a => a.notification_id === notif.id).length;
                                                                            return count > 0 ? (
                                                                                <span className="flex items-center gap-1 text-primary/70">
                                                                                    <Paperclip className="h-3 w-3" />
                                                                                    {count} fichier{count > 1 ? 's' : ''}
                                                                                </span>
                                                                            ) : null;
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="bg-slate-900 rounded-[32px] p-8 text-white shadow-2xl relative overflow-hidden group">
                                        <div className="absolute -bottom-12 -right-12 h-48 w-48 bg-emerald-500/20 rounded-full blur-3xl group-hover:bg-emerald-500/30 transition-all duration-1000"></div>
                                        <h3 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.2em] mb-8">Dernière étape</h3>
                                        <p className="text-sm text-slate-300 leading-relaxed mb-8">
                                            {mode === 'create'
                                                ? `La classe sera enregistrée en tant que brouillon. Vous pourrez la modifier librement avant de la valider. La validation déclenchera la création Google Meet, la synchronisation des étudiants et la planification des emails.`
                                                : `Les modifications seront enregistrées dans le brouillon.`
                                            }
                                        </p>
                                        <Button
                                            type="button"
                                            onClick={onSubmit}
                                            className="w-full h-16 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black text-lg rounded-2xl shadow-xl shadow-emerald-500/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? <Loader2 className="h-6 w-6 animate-spin" /> : (mode === 'create' ? "ENREGISTRER LE BROUILLON" : "ENREGISTRER")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-8 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center gap-4 rounded-b-3xl">
                    <div className="flex gap-3">
                        {currentStep > 1 && (
                            <Button
                                type="button"
                                variant="outline"
                                className="h-12 px-6 font-bold border-slate-200 rounded-xl hover:bg-white flex items-center gap-2"
                                onClick={handlePrevStep}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Retour
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            className="h-12 px-6 font-bold rounded-xl text-slate-500 hover:text-slate-900"
                            onClick={() => onOpenChange(false)}
                        >
                            Annuler
                        </Button>
                    </div>

                    <div className="flex gap-3">
                        {currentStep < 3 && (
                            <Button
                                type="button"
                                className="h-12 px-10 font-black rounded-xl shadow-xl shadow-primary/20 flex items-center gap-2 bg-primary text-white"
                                onClick={handleNextStep}
                                disabled={
                                    (currentStep === 1 && (
                                        !className.trim() || !organizerEmail.trim() || !startTime || !endTime ||
                                        (studentSource === 'learnybox' && selectedFormations.length === 0) ||
                                        (studentSource === 'csv' && csvStudents.length === 0)
                                    )) ||
                                    (currentStep === 2 && sendInvitations && !isInvitationConfigValid) ||
                                    isTransitioning
                                }
                            >
                                {currentStep === 1 ? "Emails & Séquences" : "Récapitulatif"}
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        )}

                        {/* Mobile Submit Button if needed */}
                        {currentStep === 3 && (
                            <Button
                                type="button"
                                className="h-12 px-10 font-black rounded-xl shadow-xl shadow-emerald-500/20 bg-emerald-600 hover:bg-emerald-700 text-white md:hidden"
                                onClick={onSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === 'create' ? "ENREGISTRER LE BROUILLON" : "ENREGISTRER")}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ClassConfigModal;
